import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Playlist } from '@app/library/models/playlist.model';
import { TranslateService } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import { Program } from '@radioking/programs';
import { SchedulerCoreState } from '@radioking/scheduler-core';
import { padStart } from 'lodash';
import moment from 'moment-timezone';
import { Observable, Subscription } from 'rxjs';
import { filter, first, take } from 'rxjs/operators';
import { ProgramsState } from '../../../../../programs/src/lib/states/programs.state';
import { SchedulerTrack } from '../../../../../scheduler-core/src/lib/models/scheduler-core.model';
import { BigTimeFormatLettersPipe } from '../../../../../shared/pipes/big-time-format-letters.pipe';
import {
  BreakDetails,
  BreakDetailsAndOcc,
  BreakListBreak,
  BreakListOccurrence,
} from '../../models/break.model';
import { GetBreakRequest } from '../../states/break.actions';
import { BreakState } from '../../states/break.state';

@Component({
  selector: 'rk-break-popover',
  templateUrl: './break-popover.component.html',
  styleUrls: ['./break-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreakPopoverComponent implements OnInit, AfterViewInit, OnDestroy {
  @Select(BreakState.isFetchingBreak)
  isLoading$: Observable<boolean>;

  @Select(BreakState.currentBreak)
  currentBreak$: Observable<BreakDetails>;

  track?: SchedulerTrack;

  program?: Program;

  playlist?: Playlist;

  formattedDateRange = '-';

  breakDuration: number;

  repeatWeekday = '';

  subscription = new Subscription();

  @Output()
  readonly breakEdition = new EventEmitter<BreakDetailsAndOcc>();

  @Output()
  readonly breakDeletion = new EventEmitter<BreakDetailsAndOcc>();

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly dialogRef: MatDialogRef<BreakPopoverComponent>,
    private readonly store: Store,
    private readonly translate: TranslateService,
    private readonly bigTimePipe: BigTimeFormatLettersPipe,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      occurrence: BreakListOccurrence;
      breakObj: BreakListBreak;
      el: HTMLElement;
      mobile?: boolean;
    },
  ) {}

  ngOnInit(): void {
    this.fetchBreak();
    this.subscription.add(
      this.currentBreak$
        .pipe(
          filter(b => !!b),
          take(1),
        )
        .subscribe(b => {
          this.extractBreakContent(b);
          this.extractRepeatText(b);
        }),
    );
  }

  ngAfterViewInit(): void {
    if (!this.data.mobile) {
      this.updateDialogPosition();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  updateDialogPosition(): void {
    const windowHeight = window.innerHeight;
    const { width, height } =
      this.elementRef.nativeElement.firstElementChild.getBoundingClientRect();
    const topMaxPosition = 10;
    const breakRect = this.data.el.getBoundingClientRect();

    // Center-left in window initially
    let x = breakRect.x - width;
    let y = windowHeight / 2 - height / 2;

    // Move to right if overflow on left
    if (x < 0) {
      x = breakRect.x + breakRect.width;
    }

    // Dialog's top position cannot be below break's top position (align dialog and break on top)
    if (y > breakRect.y + 20) {
      y = breakRect.y;
    }

    // Dialog's bottom position cannot be above break's top position (stick dialog to break on corners)
    if (y + height / 2 < breakRect.y) {
      y = breakRect.y - height - 60;
    }

    // Prevent overflow on top
    if (y < topMaxPosition) {
      y = topMaxPosition;
    }

    this.dialogRef.updatePosition({
      left: `${x}px`,
      top: `${y}px`,
    });
  }

  extractBreakContent(currentBreak: BreakDetails): void {
    if (currentBreak?.programId) {
      this.program = this.store.selectSnapshot(ProgramsState.programWithId)(
        currentBreak.programId,
      );
      this.breakDuration = this.program.estimatedDuration;
    }
    if (currentBreak?.playlistId) {
      this.playlist = this.store.selectSnapshot(SchedulerCoreState.playlistWithId)(
        currentBreak?.playlistId,
      );
      this.breakDuration =
        (this.playlist.duration / this.playlist.nbTracks) * currentBreak.tracksCount;
    }
    if (currentBreak?.trackId) {
      this.track = this.store.selectSnapshot(SchedulerCoreState.trackWithId)(
        currentBreak.trackId,
      );
      this.breakDuration = this.track.duration;
    }
    this.formatDate();
  }

  /**
   * Extract repeat text from break
   */
  extractRepeatText(currentBreak: BreakDetails): void {
    this.repeatWeekday = this.translate
      .instant('SCHEDULE.PLANNING.EDIT_MODAL_LABEL_ENDS_ON')
      .toLowerCase();
    // Cast to string in case of only 1 day
    const days = `${currentBreak.repeat.repeatDays}`.split(',').filter(d => !!d);
    days.forEach((d, i) => {
      this.repeatWeekday += moment().day(d).format('dddd');
      if (i + 2 < days.length) {
        this.repeatWeekday += ', ';
      } else if (i + 2 === days.length) {
        this.repeatWeekday += ` ${this.translate.instant(
          'MEDIA.FILTERS.NUMERIC_INPUT.AND',
        )} `;
      }
    });
  }

  /**
   * Format break date range
   */
  formatDate(): void {
    const startDate = this.data.occurrence.startDate;
    let formattedDuration = this.bigTimePipe.transform(this.breakDuration, true);
    if (!this.track && this.breakDuration > 0) {
      formattedDuration = this.translate.instant(
        'SCHEDULE.PLANNING.BREAK_POPOVER_APPROX_DURATION',
        { duration: formattedDuration },
      );
    }

    const formattedDate = this.translate.instant('SCHEDULE.PLANNING.BREAK_POPOVER_DATE', {
      date:
        startDate.format('llll').replace(startDate.format('lll'), '') +
        startDate.format('ll'),
      hour: startDate.format('LT'),
    });

    this.formattedDateRange = formattedDate + ' (' + formattedDuration + ')';
  }

  /**
   * "Edit" button clicked
   */
  editionClicked(): void {
    this.currentBreak$.pipe(first()).subscribe(breakObj => {
      this.currentBreak$.pipe(first()).subscribe(breakObj => {
        this.breakEdition.emit({
          break: breakObj,
          breakName: this.data.breakObj.name,
          occurrence: null,
        });
      });
    });
  }

  /**
   * "Delete" button clicked
   */
  deletionClicked(): void {
    this.currentBreak$.pipe(first()).subscribe(breakObj => {
      this.breakDeletion.emit({
        break: breakObj,
        breakName: this.data.breakObj.name,
        occurrence: this.data.occurrence.occurrenceId,
      });
    });
  }

  /**
   * Close self (closing dialog)
   */
  closeSelf(): void {
    this.dialogRef.close();
  }

  /**
   * Format a break time
   */
  formatBreakTime(date: moment.Moment): string {
    if (!date) {
      return '';
    }

    const hours = date.hours() || '00';
    const minutes = date.minutes() ? padStart(`${date.minutes()}`, 2, '0') : '';

    return `${hours}h${minutes}`;
  }

  /**
   * Get a break's title, depending on its kind (track, program or playlist)
   */
  getBreakTitle(breakObj: BreakDetails): string {
    return 'title';
    /*const programs = this.store.selectSnapshot(ProgramsState.allProgramsForDropdown);
    const playlists = this.store.selectSnapshot(
      SchedulerCoreState.allPlaylistsForDropdown,
    );

    return breakObj.track
      ? [breakObj.track.title, breakObj.track.artist].filter(v => v).join(' - ')
      : breakObj.program
      ? programs.find(v => v.id === breakObj.program).name
      : playlists.find(v => v.id === breakObj.playlist).name;
      */
  }

  @Dispatch()
  fetchBreak() {
    return new GetBreakRequest(this.data.breakObj.id);
  }
}
